import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

type User = { token: string | null; email?: string };

type AuthContextType = {
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User>>;
  logout: () => void;
};

const AuthCtx = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  // ✅ initialize from localStorage
  const [user, setUser] = useState<User>(() => {
    const token = localStorage.getItem("pc_token");
    const email = localStorage.getItem("pc_email") || undefined;
    return { token, email };
  });

  // ✅ keep localStorage in sync whenever user changes
  useEffect(() => {
    if (user.token) {
      localStorage.setItem("pc_token", user.token);
      if (user.email) {
        localStorage.setItem("pc_email", user.email);
      }
    } else {
      localStorage.removeItem("pc_token");
      localStorage.removeItem("pc_email");
    }
  }, [user]);

  // ✅ logout clears both state and storage
  const logout = () => {
    setUser({ token: null, email: undefined });
    localStorage.removeItem("pc_token");
    localStorage.removeItem("pc_email");
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}
